import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BarChart3, CheckCircle2, FileWarning, Filter, RefreshCw, Search } from 'lucide-react';
import { getPendencias, getRejeicoes, PendenciaItem, RejeicaoItem } from '../api/pendencias';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import { formatDateTime } from '../utils/formatters';

const SEVERIDADE_CLASS: Record<string, string> = {
  alta: 'badge-danger',
  media: 'badge-warning',
  baixa: 'badge-success',
};

const TIPO_LABEL: Record<string, string> = {
  nfe: 'NF-e',
  cte: 'CT-e',
  cte_os: 'CT-e OS',
  mdfe: 'MDF-e',
  dfe: 'DF-e',
  certificado: 'Certificado',
};

const CATEGORIA_LABEL: Record<string, string> = {
  rejeicao: 'Rejeição',
  sefaz: 'SEFAZ',
  transmissao: 'Transmissão',
  xml: 'XML',
  pdf: 'PDF',
  rascunho: 'Rascunho',
  dfe: 'DF-e',
  dfe_resumo: 'Resumo DF-e',
  certificado: 'Certificado',
};

function tipoLabel(tipo: string) {
  return TIPO_LABEL[tipo] || tipo.toUpperCase();
}

function categoriaLabel(categoria: string) {
  return CATEGORIA_LABEL[categoria] || categoria.replace(/_/g, ' ');
}

function docLabel(p: PendenciaItem) {
  if (p.tipo === 'dfe') return `${tipoLabel(p.tipo)} ${p.numero || `#${p.id}`}`;
  const numero = p.numero ? ` ${p.numero}` : ` #${p.id}`;
  const serie = p.serie ? ` série ${p.serie}` : '';
  return `${tipoLabel(p.tipo)}${numero}${serie}`;
}

function rejeicaoLabel(p: RejeicaoItem) {
  const numero = p.numero ? ` ${p.numero}` : ` #${p.id}`;
  const serie = p.serie ? ` série ${p.serie}` : '';
  return `${tipoLabel(p.tipo)}${numero}${serie}`;
}

export default function PendenciasPage() {
  const { emitenteId } = useEmitenteAtivo();
  const [aba, setAba] = useState<'pendencias' | 'rejeicoes'>('pendencias');
  const [busca, setBusca] = useState('');
  const [severidade, setSeveridade] = useState('todas');
  const [categoria, setCategoria] = useState('todas');

  const q = useQuery({
    queryKey: ['pendencias', emitenteId],
    queryFn: () => getPendencias(emitenteId),
    refetchOnWindowFocus: false,
  });

  const rejeicoesQ = useQuery({
    queryKey: ['rejeicoes-cstat', emitenteId],
    queryFn: () => getRejeicoes(emitenteId),
    refetchOnWindowFocus: false,
  });

  const itens = q.data?.itens || [];
  const resumo = q.data?.resumo || { total: 0, alta: 0, media: 0, baixa: 0, rejeicoes: 0, rascunhos: 0, dfe: 0, xml_pdf: 0, certificados: 0 };
  const rejeicoes = rejeicoesQ.data?.itens || [];
  const resumoRejeicoes = rejeicoesQ.data?.resumo || { total: 0, por_cstat: {}, por_tipo: {} };

  const categorias = useMemo(() => Array.from(new Set(itens.map((i) => i.categoria))).sort(), [itens]);

  const filtrados = useMemo(() => {
    const qBusca = busca.trim().toLowerCase();
    return itens.filter((item) => {
      if (severidade !== 'todas' && item.severidade !== severidade) return false;
      if (categoria !== 'todas' && item.categoria !== categoria) return false;
      if (!qBusca) return true;
      return [
        tipoLabel(item.tipo),
        categoriaLabel(item.categoria),
        item.status,
        item.c_stat_sefaz,
        item.x_motivo_sefaz,
        item.mensagem,
        item.chave,
        item.numero,
        item.serie,
      ].some((v) => String(v || '').toLowerCase().includes(qBusca));
    });
  }, [busca, categoria, itens, severidade]);

  const rejeicoesFiltradas = useMemo(() => {
    const qBusca = busca.trim().toLowerCase();
    return rejeicoes.filter((item) => {
      if (!qBusca) return true;
      return [
        tipoLabel(item.tipo),
        item.origem,
        item.status,
        item.c_stat_sefaz,
        item.x_motivo_sefaz,
        item.chave,
        item.numero,
        item.serie,
        item.tipo_evento,
        item.nome_evento,
      ].some((v) => String(v || '').toLowerCase().includes(qBusca));
    });
  }, [busca, rejeicoes]);

  const cstats = Object.entries(resumoRejeicoes.por_cstat).sort((a, b) => b[1] - a[1]);
  const tiposRejeicao = Object.entries(resumoRejeicoes.por_tipo).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="eyebrow">Operação fiscal</div>
            <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">Pendências</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
              Rejeições, rascunhos, XML/PDF ausentes, DF-e pendente e certificados que exigem ação.
            </p>
          </div>
          <button className="btn-secondary" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={`h-4 w-4 ${q.isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </header>

      <div className="inline-flex rounded-xl border border-slate-200 bg-white/90 p-1 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
        <button type="button" className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${aba === 'pendencias' ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200' : 'text-slate-600 hover:text-slate-950 dark:text-slate-400 dark:hover:text-slate-100'}`} onClick={() => setAba('pendencias')}>
          Pendências
        </button>
        <button type="button" className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${aba === 'rejeicoes' ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200' : 'text-slate-600 hover:text-slate-950 dark:text-slate-400 dark:hover:text-slate-100'}`} onClick={() => setAba('rejeicoes')}>
          cStat / rejeições
        </button>
      </div>

      {aba === 'pendencias' ? (
        <section className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
          <div className="card-surface p-4">
            <div className="eyebrow">Total</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumo.total}</div>
          </div>
          <div className="card-surface border-rose-200 p-4 dark:border-rose-500/20">
            <div className="eyebrow">Alta</div>
            <div className="mono mt-1 text-2xl text-rose-700 dark:text-rose-300">{resumo.alta}</div>
          </div>
          <div className="card-surface border-amber-200 p-4 dark:border-amber-500/20">
            <div className="eyebrow">Média</div>
            <div className="mono mt-1 text-2xl text-amber-700 dark:text-amber-300">{resumo.media}</div>
          </div>
          <div className="card-surface p-4">
            <div className="eyebrow">Baixa</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumo.baixa}</div>
          </div>
          <div className="card-surface p-4">
            <div className="eyebrow">Rejeições</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumo.rejeicoes}</div>
          </div>
          <div className="card-surface p-4">
            <div className="eyebrow">Rascunhos</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumo.rascunhos}</div>
          </div>
          <div className="card-surface p-4">
            <div className="eyebrow">DF-e</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumo.dfe}</div>
          </div>
          <div className="card-surface p-4">
            <div className="eyebrow">Certificados</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumo.certificados || 0}</div>
          </div>
        </section>
      ) : (
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="card-surface p-4">
            <div className="eyebrow">Registros com cStat pendente</div>
            <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{resumoRejeicoes.total}</div>
          </div>
          <div className="card-surface p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100"><BarChart3 className="h-4 w-4" /> Por cStat</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {cstats.length === 0 ? <span className="text-sm text-neutral-600 dark:text-neutral-400">Sem rejeições</span> : cstats.map(([codigo, total]) => (
                <span key={codigo} className="badge-warning">cStat {codigo}: {total}</span>
              ))}
            </div>
          </div>
          <div className="card-surface p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100"><BarChart3 className="h-4 w-4" /> Por tipo</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {tiposRejeicao.length === 0 ? <span className="text-sm text-neutral-600 dark:text-neutral-400">Sem rejeições</span> : tiposRejeicao.map(([tipo, total]) => (
                <span key={tipo} className="badge-neutral">{tipoLabel(tipo)}: {total}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="card-surface space-y-3 p-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-neutral-600 dark:text-neutral-400" />
          <h2 className="font-semibold text-slate-950 dark:text-slate-100">Filtros</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="relative md:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input className="field-input pl-9" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por chave, status, motivo ou número" />
          </div>
          {aba === 'pendencias' && <select className="field-input" value={severidade} onChange={(e) => setSeveridade(e.target.value)}>
            <option value="todas">Todas severidades</option>
            <option value="alta">Alta</option>
            <option value="media">Média</option>
            <option value="baixa">Baixa</option>
          </select>}
          {aba === 'pendencias' && <select className="field-input" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="todas">Todas categorias</option>
            {categorias.map((c) => <option key={c} value={c}>{categoriaLabel(c)}</option>)}
          </select>}
        </div>
      </section>

      {aba === 'rejeicoes' ? (
        <section className="card-surface overflow-hidden">
          {rejeicoesQ.isLoading ? (
            <div className="grid place-items-center py-12"><LoadingSpinner /></div>
          ) : rejeicoesFiltradas.length === 0 ? (
            <div className="py-12 text-center text-sm text-neutral-600 dark:text-neutral-400">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600 dark:text-emerald-500" />
              Nenhum cStat problemático encontrado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase text-neutral-600 dark:bg-white/[0.03] dark:text-neutral-400">
                  <tr>
                    <th className="px-4 py-3">Documento/evento</th>
                    <th className="px-4 py-3">cStat</th>
                    <th className="px-4 py-3">Motivo</th>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {rejeicoesFiltradas.map((p, idx) => (
                    <tr key={`${p.origem}-${p.tipo}-${p.id}-${p.evento_id || idx}`} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-950 dark:text-slate-100">{rejeicaoLabel(p)}</div>
                        <div className="text-xs text-neutral-600 dark:text-neutral-400">
                          {p.origem === 'evento' ? `Evento ${p.tipo_evento || '-'} ${p.nome_evento || ''}` : p.status || '-'}, {p.ambiente || '-'}
                        </div>
                        {p.chave && <div className="max-w-[320px] truncate text-xs text-neutral-600 dark:text-neutral-400">{p.chave}</div>}
                      </td>
                      <td className="px-4 py-3"><span className="badge-warning">cStat {p.c_stat_sefaz || '-'}</span></td>
                      <td className="max-w-[520px] px-4 py-3 text-neutral-700 dark:text-neutral-300">{p.x_motivo_sefaz || '-'}</td>
                      <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">{formatDateTime(p.data)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link className="btn-secondary inline-flex" to={p.url}>
                          <FileWarning className="h-4 w-4" />
                          Abrir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className="card-surface overflow-hidden">
          {q.isLoading ? (
            <div className="grid place-items-center py-12"><LoadingSpinner /></div>
          ) : filtrados.length === 0 ? (
            <div className="py-12 text-center text-sm text-neutral-600 dark:text-neutral-400">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600 dark:text-emerald-500" />
              Nenhuma pendência encontrada para os filtros atuais.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase text-neutral-600 dark:bg-white/[0.03] dark:text-neutral-400">
                  <tr>
                    <th className="px-4 py-3">Documento</th>
                    <th className="px-4 py-3">Pendência</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {filtrados.map((p, idx) => (
                    <tr key={`${p.tipo}-${p.id}-${p.categoria}-${idx}`} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-950 dark:text-slate-100">{docLabel(p)}</div>
                        <div className="text-xs text-neutral-600 dark:text-neutral-400">{p.ambiente || '-'} {p.modelo ? `modelo ${p.modelo}` : ''}</div>
                        {p.chave && <div className="max-w-[320px] truncate text-xs text-neutral-600 dark:text-neutral-400">{p.chave}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                          <span className={SEVERIDADE_CLASS[p.severidade] || 'badge-neutral'}>{p.severidade}</span>
                          <span className="text-xs text-neutral-600 dark:text-neutral-400">{categoriaLabel(p.categoria)}</span>
                        </div>
                        <div className="mt-1 max-w-[520px] text-neutral-700 dark:text-neutral-300">{p.mensagem}</div>
                      </td>
                      <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">
                        <div>{p.status || '-'}</div>
                        {(p.c_stat_sefaz || p.x_motivo_sefaz) && (
                          <div className="text-xs text-neutral-600 dark:text-neutral-400">
                            {p.c_stat_sefaz ? `cStat ${p.c_stat_sefaz}` : ''} {p.x_motivo_sefaz || ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">{formatDateTime(p.data)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link className="btn-secondary inline-flex" to={p.url}>
                          <FileWarning className="h-4 w-4" />
                          Abrir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
